/**
 * TrialBanner
 *
 * Richer trial-state banner shown at the top of `SubscriptionsBundlingPage`
 * for both Solo and Group practices while `subscription.is_trialing === true`.
 * Replaces the terse status Alert (which stays in place for non-trial orgs).
 *
 * Surfaces:
 *   - Days remaining + trial end date
 *   - Contextual subtext (changes when no tier is selected yet)
 *   - "Add payment method" / "Update payment method" CTA, prominent
 */

import { CalendarMonth } from '@mui/icons-material';
import { Alert, Box, Button, Typography } from '@mui/material';
import type { FullSubscriptionDetailsResponse } from '@store/generated/subscriptions';

interface TrialBannerProps {
  subscription: FullSubscriptionDetailsResponse;
  onUpdatePaymentMethod: () => void;
}

const formatTrialEnd = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export function TrialBanner({ subscription, onUpdatePaymentMethod }: TrialBannerProps) {
  const daysRemaining = subscription.trial_days_remaining ?? null;
  const trialEnd = formatTrialEnd(subscription.trial_end);
  const hasPaymentMethod = !!subscription.payment_method?.has_payment_method;

  // Determine if no tier has been selected yet — drives the contextual suffix
  // ("Choose a tier below…") so users know what to do next.
  const hasTier = (subscription.line_items ?? []).some(
    item => item.bundle_type === 'tier',
  );

  const headlinePrefix =
    daysRemaining !== null && daysRemaining > 0
      ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left in your free trial`
      : daysRemaining === 0
        ? 'Your free trial ends today'
        : 'Your free trial is active';

  return (
    <Alert
      severity="info"
      icon={<CalendarMonth />}
      action={
        <Button
          variant={hasPaymentMethod ? 'outlined' : 'contained'}
          color="primary"
          size="small"
          onClick={onUpdatePaymentMethod}
          sx={{ minWidth: 160, fontWeight: 600 }}
        >
          {hasPaymentMethod ? 'Update payment method' : 'Add payment method'}
        </Button>
      }
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'info.main',
        '& .MuiAlert-action': { marginRight: 0, alignItems: 'center' },
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {headlinePrefix}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          You have full access to all features
          {trialEnd ? ` until ${trialEnd}` : ''}.
          {!hasTier && ' Choose a tier below to set what you’ll pay when your trial ends.'}
        </Typography>
      </Box>
    </Alert>
  );
}
